import axios from 'axios';
import debounce from 'lodash.debounce';

// Import necessary Chakra UI components, icons, and utilities
import { RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Flex,
  Spacer,
  Text,
  IconButton,
  InputGroup,
  InputRightElement,
  Input,
  Divider,
  Spinner,
  useColorModeValue,
  useDisclosure,
  Collapse,
  Button,
} from '@chakra-ui/react';

import { FaFilter, FaFileDownload, FaFileImport } from 'react-icons/fa';

// Import PropTypes for type checking
import PropTypes from 'prop-types';

// Import DataTable for rendering tabular data
import DataTable from 'react-data-table-component';

// Import Redux utilities
import { ReducerStatus } from 'utils/constants';
import { useDispatch } from 'react-redux';

import { RemoveProgressionButton } from 'utils/columns/reports/progressionColumns';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import { AsyncSelect, Select } from 'chakra-react-select';
import { statusOptions } from 'utils/constants/reportStatus';
import { subQuestReportActions } from 'store/modules/subQuestReport/subQuest-report-slice';

// Main component for rendering the SubQuestRecords table
function SubQuestRecords({
  subQuest,
  status,
  data,
  columns,
  totalItems,
  searchText,
  selectedUser,
  selectedCourse,
  selectedQuest,
  selectedResult,
  handleUserChange,
  handleCourseChange,
  handleQuestChange,
  handleResultChange,
  handlePerRowsChange,
  handleSort,
  handlePageChange,
  handleSearchTextChange,
  handleReloadData,
  ...rest
}) {
  const dispatch = useDispatch();

  // Determine the theme for the table based on the color mode
  const tableTheme = useColorModeValue('default', 'dark');

  const { isOpen, onToggle } = useDisclosure();

  // Show filters only for File Submission sub-quests (type = 5)
  const showFilters = subQuest?.type?.id === 5;

  /**
   * Loads users from API with debouncing to reduce unnecessary requests
   * @param {string} inputValue - Search query for user names
   * @param {function} callback - Callback to return formatted user options
   */
  const loadUsers = debounce((inputValue, callback) => {
    axios
      .get(`/users?query=${inputValue.trim()}&pageSize=5`)
      .then((response) => {
        callback(
          response.data.data?.map((user) => ({
            label: user.fullName,
            value: user.fullName,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  /**
   * Loads courses from API with debouncing to reduce unnecessary requests
   * @param {string} inputValue - Search query for course names
   * @param {function} callback - Callback to return formatted course options
   */
  const loadCourses = debounce((inputValue, callback) => {
    axios
      .get(`/courses?query=${inputValue.trim()}&pageSize=5`)
      .then((response) => {
        callback(
          response.data.data?.map((course) => ({
            label: course.name,
            value: course.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  /**
   * Loads quests from API with debouncing to reduce unnecessary requests
   * @param {string} inputValue - Search query for quest names
   * @param {function} callback - Callback to return formatted quest options
   */
  const loadQuests = debounce((inputValue, callback) => {
    axios
      .get(`/admin/quests?query=${inputValue.trim()}&pageSize=5`)
      .then((response) => {
        callback(
          response.data.data?.map((quest) => ({
            label: quest.name,
            value: quest.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  /**
   * Handles export button click
   * Extracts user IDs from current data and opens export modal
   */
  const handleExportButton = () => {
    // Extract user IDs from current progression data
    const userIds = data.map((progression) => progression.ownerId);

    dispatch(
      subQuestReportActions.setExportSubmissionSessions({
        modal: true,
        userIds,
        subQuestId: subQuest?.id,
      })
    );
  };

  /**
   * Handles import button click
   * Opens the import modal for the current sub-quest
   */
  const handleImportButton = () => {
    dispatch(
      subQuestReportActions.setImportSubmissionSessions({
        modal: true,
        subQuestId: subQuest?.id,
      })
    );
  };

  return (
    <Box p={4} borderWidth="1px" {...rest}>
      {/* Header section with title, reload button, and search input */}
      <Flex m={2} align="center">
        <Text as="samp" fontSize="xs">
          <strong>USER PROGRESSION RECORDS</strong>
        </Text>
        <Spacer />

        {showFilters && (
          <>
            <Button
              mr={2}
              leftIcon={<FaFileImport />}
              colorScheme="teal"
              variant="outline"
              size="sm"
              onClick={() => handleImportButton()}
            >
              Import
            </Button>

            <Button
              leftIcon={<FaFileDownload />}
              colorScheme="blue"
              variant="outline"
              size="sm"
              onClick={() => handleExportButton()}
            >
              Export
            </Button>
          </>
        )}

        {/* Reload button */}
        <IconButton
          onClick={handleReloadData}
          aria-label="Reload table"
          variant="link"
          icon={<RepeatIcon />}
        />

        {/* Search input */}
        <InputGroup width={{ base: '100%', md: '350px' }}>
          <InputRightElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputRightElement>
          <Input
            value={searchText}
            onChange={handleSearchTextChange}
            name="query"
            placeholder="Search"
          />
        </InputGroup>

        {/* Filter Button */}
        {showFilters && (
          <IconButton
            ms={3}
            variant="outline"
            colorScheme="gray"
            aria-label="filter items"
            icon={<FaFilter />}
            onClick={onToggle}
          />
        )}
      </Flex>

      {/* Filter Fields */}
      {showFilters && (
        <Collapse in={isOpen} style={{ overflow: 'unset' }}>
          <Flex mt={4} mx={2} align="center" gap={3}>
            {/* User Filter */}
            <FormikFormControl label="User" helperText="">
              <AsyncSelect
                placeholder="Search user"
                loadOptions={loadUsers}
                value={selectedUser}
                onChange={handleUserChange}
                isClearable
              />
            </FormikFormControl>

            {/* Course Filter */}
            <FormikFormControl label="Course" helperText="">
              <AsyncSelect
                placeholder="Search course"
                loadOptions={loadCourses}
                value={selectedCourse}
                onChange={handleCourseChange}
                isClearable
              />
            </FormikFormControl>

            {/* Quest Filter */}
            <FormikFormControl label="Quest" helperText="">
              <AsyncSelect
                placeholder="Search quest"
                loadOptions={loadQuests}
                value={selectedQuest}
                onChange={handleQuestChange}
                isClearable
              />
            </FormikFormControl>

            {/* Status Filter */}
            <FormikFormControl label="Status" helperText="">
              <Select
                placeholder="Select status"
                options={statusOptions}
                value={selectedResult}
                onChange={handleResultChange}
                isClearable
              />
            </FormikFormControl>
          </Flex>
        </Collapse>
      )}

      <Divider mt={4} />

      {/* DataTable component for displaying records */}
      <DataTable
        theme={tableTheme}
        progressPending={status === ReducerStatus.LOADING}
        columns={[...columns, RemoveProgressionButton()]} // Add the RemoveProgressionButton column
        data={data}
        paginationTotalRows={totalItems}
        onChangeRowsPerPage={handlePerRowsChange}
        onSort={handleSort}
        onChangePage={handlePageChange}
        pagination
        paginationServer
        sortServer
        progressComponent={
          <Center my={10}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
          </Center>
        }
      />
    </Box>
  );
}

// Default props for the component
SubQuestRecords.defaultProps = {
  subQuest: null,
  totalItems: 0,
  selectedUser: null,
  selectedCourse: null,
  selectedQuest: null,
  selectedResult: null,
};

// PropTypes for type checking
SubQuestRecords.propTypes = {
  subQuest: PropTypes.object,
  status: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  totalItems: PropTypes.number,
  searchText: PropTypes.string.isRequired,
  selectedUser: PropTypes.object,
  selectedCourse: PropTypes.object,
  selectedQuest: PropTypes.object,
  selectedResult: PropTypes.object,
  handleUserChange: PropTypes.func.isRequired,
  handleCourseChange: PropTypes.func.isRequired,
  handleQuestChange: PropTypes.func.isRequired,
  handleResultChange: PropTypes.func.isRequired,
  handlePerRowsChange: PropTypes.func.isRequired,
  handleSort: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleSearchTextChange: PropTypes.func.isRequired,
  handleReloadData: PropTypes.func.isRequired,
};

// Export the component as default
export default SubQuestRecords;
